import { useEffect, useState } from "react";
import { api, API, idr } from "../../lib/api";
import { toast } from "sonner";
import { FileBarChart2, Scale, TrendingUp, TrendingDown, Download, FileSpreadsheet, Loader2, Send } from "lucide-react";
import { hariIniISO, bulanIniISO } from "../../lib/tanggal";

// Helper unduh berkas biner (PDF/XLSX) dari endpoint export — dipakai semua
// tombol ekspor di halaman ini supaya perilaku (blob → object URL → klik →
// revoke) SAMA persis, bukan disalin ulang tiap tombol dengan potensi beda halus.
async function _downloadBlob(url, filename, mime) {
  const res = await api.get(url, { responseType: "blob" });
  const blob = new Blob([res.data], { type: mime });
  const objUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objUrl);
}

const TABS = [
  { key: "lr",    label: "Laba Rugi",   icon: TrendingUp },
  { key: "nr",    label: "Neraca",      icon: Scale },
  { key: "cmp",   label: "Perbandingan",icon: Scale },
  { key: "cf",    label: "Arus Kas",    icon: TrendingDown },
];

export default function FinanceLaporan() {
  const [tab, setTab] = useState("lr");
  // `bulanIniISO()` — `.toISOString().slice(0,7)` (UTC) memberi BULAN
  // SEBELUMNYA pada tanggal 1 sebelum jam 07:00 WIB (lihat lib/tanggal.js).
  const [periode, setPeriode] = useState(bulanIniISO());

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-laporan">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Laporan Keuangan</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Laba Rugi · Neraca · Arus Kas</h1>
          <p className="text-sm text-slate-600 mt-1">Auto-generated dari semua transaksi. Real-time.</p>
        </div>
        <input type="month" value={periode} onChange={e => setPeriode(e.target.value)} data-testid="lap-periode" className="h-10 px-3 border border-slate-200 rounded-lg text-sm" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-1 inline-flex">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key} onClick={() => setTab(t.key)} data-testid={`lap-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 h-9 rounded-lg text-sm font-medium transition ${
                tab === t.key ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "lr" && <LabaRugi periode={periode} />}
      {tab === "nr" && <Neraca periode={periode} />}
      {tab === "cmp" && <NeracaCompare />}
      {tab === "cf" && <CashFlow periode={periode} />}
    </div>
  );
}

function NeracaCompare() {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    api.get("/finance/neraca/comparison").then(r => setData(r.data)).catch(() => toast.error("Gagal load"));
  }, []);

  const exportPdf = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = notes.trim() ? `?notes=${encodeURIComponent(notes.trim())}` : "";
      const res = await api.get(`/finance/neraca/comparison/export-pdf${params}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // `hariIniISO()`, bukan `.toISOString()` (UTC) — kosmetik (nama berkas),
      // tapi sekalian dibenahi supaya konsisten dengan seluruh berkas ini.
      const today = hariIniISO();
      a.download = `neraca-perbandingan-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF berhasil di-download 📄");
    } catch (e) {
      toast.error("Gagal export PDF");
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  const sendToBoard = async () => {
    if (!window.confirm("Kirim Neraca Perbandingan PDF ke Telegram board sekarang?")) return;
    try {
      const res = await api.post("/finance/neraca/comparison/trigger-send");
      toast.success(res.data?.message || "Terkirim ke Telegram ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal kirim ke board");
    }
  };

  if (!data) return <div className="text-slate-500 text-sm">Memuat perbandingan...</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="neraca-comparison">
      <div className="px-5 py-3 border-b border-slate-100 flex items-start justify-between gap-3 flex-col md:flex-row">
        <div>
          <p className="font-display font-bold text-lg text-slate-900">Perbandingan Neraca 3 Periode</p>
          <p className="text-xs text-slate-500 mt-0.5">{data.snapshots.map(s => `${s.label} (${s.tanggal})`).join(" · ")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowNotes(v => !v)}
            data-testid="cmp-toggle-notes"
            className="h-9 px-3 rounded-lg text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
          >
            {showNotes ? "Sembunyikan Notes" : "+ Catatan CEO"}
          </button>
          <button
            onClick={sendToBoard}
            data-testid="cmp-send-board"
            title="Kirim PDF via Telegram ke board (auto-scheduled tgl 1 jam 09:05)"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-xs font-medium border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition"
          >
            <Send className="w-3.5 h-3.5" /> Kirim ke Board
          </button>
          <button
            onClick={exportPdf}
            disabled={exporting}
            data-testid="cmp-export-pdf"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg text-xs font-semibold bg-emerald-700 text-white hover:bg-emerald-800 shadow-sm transition disabled:opacity-50"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {exporting ? "Generating..." : "Export ke PDF"}
          </button>
        </div>
      </div>
      {showNotes && (
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
          <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Catatan Manajemen (opsional)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            data-testid="cmp-notes-input"
            placeholder="Contoh: Kenaikan aset Q1 didorong akuisisi 2 unit properti di Bandung..."
            rows={3}
            className="mt-1 w-full text-sm p-2.5 border border-slate-200 rounded-lg focus:border-emerald-500 focus:outline-none resize-y"
          />
          <p className="text-[10px] text-slate-400 mt-1">Notes ini akan tampil di bagian bawah PDF untuk konteks investor/board.</p>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-[11px] uppercase text-slate-500"><tr>
            <th className="px-3 py-2 text-left sticky left-0 bg-slate-50">Akun</th>
            <th className="px-3 py-2 text-right">Hari Ini</th>
            <th className="px-3 py-2 text-right">Bulan Lalu</th>
            <th className="px-3 py-2 text-center">Δ Bln</th>
            <th className="px-3 py-2 text-right">3 Bln Lalu</th>
            <th className="px-3 py-2 text-center">Δ Kuartal</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r, i) => {
              const bold = /TOTAL/.test(r.label);
              return (
                <tr key={i} className={`border-b border-slate-50 ${bold ? "bg-slate-50 font-semibold" : "hover:bg-slate-50/50"}`}>
                  <td className="px-3 py-2 sticky left-0 bg-inherit text-slate-800">{r.label}</td>
                  <td className="px-3 py-2 text-right font-mono">{idr(r.hari_ini)}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-600">{idr(r.bulan_lalu)}</td>
                  <td className={`px-3 py-2 text-center text-xs font-semibold ${r.delta_vs_bulan_lalu.arrow === "▲" ? "text-emerald-700" : r.delta_vs_bulan_lalu.arrow === "▼" ? "text-rose-700" : "text-slate-500"}`}>
                    {r.delta_vs_bulan_lalu.arrow} {r.delta_vs_bulan_lalu.pct}%
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-slate-600">{idr(r.kuartal_lalu)}</td>
                  <td className={`px-3 py-2 text-center text-xs font-semibold ${r.delta_vs_kuartal_lalu.arrow === "▲" ? "text-emerald-700" : r.delta_vs_kuartal_lalu.arrow === "▼" ? "text-rose-700" : "text-slate-500"}`}>
                    {r.delta_vs_kuartal_lalu.arrow} {r.delta_vs_kuartal_lalu.pct}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LabaRugi({ periode }) {
  const [data, setData] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  useEffect(() => {
    api.get(`/finance/laba-rugi?periode=${periode}`).then(r => setData(r.data)).catch(() => toast.error("Gagal memuat"));
  }, [periode]);

  const exportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      await _downloadBlob(`/finance/laba-rugi/export-pdf?periode=${periode}`, `laba-rugi-${periode}.pdf`, "application/pdf");
      toast.success("PDF Laba Rugi berhasil di-download 📄");
    } catch (e) {
      toast.error("Gagal export PDF Laba Rugi");
      console.error(e);
    } finally {
      setExportingPdf(false);
    }
  };

  const exportXlsx = async () => {
    if (exportingXlsx) return;
    setExportingXlsx(true);
    try {
      await _downloadBlob(`/finance/laba-rugi/export-xlsx?periode=${periode}`, `laba-rugi-${periode}.xlsx`,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      toast.success("Excel Laba Rugi berhasil di-download 📊");
    } catch (e) {
      toast.error("Gagal export Excel Laba Rugi");
      console.error(e);
    } finally {
      setExportingXlsx(false);
    }
  };

  if (!data) return <div className="text-slate-500 text-sm">Memuat...</div>;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="font-display font-bold text-lg text-slate-900">Laporan Laba Rugi</p>
          <p className="text-xs text-slate-500">Periode {data.periode}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportXlsx}
            disabled={exportingXlsx}
            data-testid="lap-lr-export-xlsx"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm transition disabled:opacity-50"
          >
            {exportingXlsx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
            {exportingXlsx ? "Menyiapkan..." : "Export Excel"}
          </button>
          <button
            onClick={exportPdf}
            disabled={exportingPdf}
            data-testid="lap-lr-export-pdf"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold bg-emerald-700 text-white hover:bg-emerald-800 shadow-sm transition disabled:opacity-50"
          >
            {exportingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {exportingPdf ? "Menyiapkan..." : "Export PDF"}
          </button>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <Section title="Pendapatan" total={data.pendapatan.total} breakdown={data.pendapatan.breakdown} tone="emerald" />
        <Row label="Total Pendapatan" value={data.pendapatan.total} bold tone="emerald" />
        <Section title="Harga Pokok Penjualan" total={data.hpp.total} breakdown={data.hpp.breakdown} tone="orange" />
        {data.hpp.estimasi && data.hpp.keterangan && (
          <div data-testid="lap-hpp-keterangan" className="ml-4 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
            {data.hpp.keterangan}
          </div>
        )}
        <Row label="Laba Kotor" value={data.laba_kotor} bold sub={`Margin ${data.margin_kotor_pct}%`} tone={data.laba_kotor > 0 ? "emerald" : "rose"} highlight />
        <Section title="Beban Operasional" total={data.beban.total} breakdown={data.beban.breakdown} tone="rose" />
        <Row label="Laba Bersih" value={data.laba_bersih} bold big sub={`Margin ${data.margin_bersih_pct}%`} tone={data.laba_bersih > 0 ? "emerald" : "rose"} highlight />
        <div className={`mt-4 p-3 rounded-lg border text-sm ${data.laba_bersih > 0 ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
          {data.kesimpulan}
        </div>
      </div>
    </div>
  );
}

function Section({ title, total, breakdown, tone }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">{title}</p>
      {(breakdown || []).length === 0 ? (
        <p className="pl-4 text-sm text-slate-400">Belum ada</p>
      ) : (breakdown || []).map(b => (
        <div key={b.akun_code || b.akun_name} className="flex justify-between items-center py-1.5 pl-4 pr-2 hover:bg-slate-50 rounded">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-mono text-slate-400">{b.akun_code}</span>
            <p className="text-sm text-slate-700 truncate">{b.akun_name}</p>
            {b.estimasi && (
              <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Estimasi</span>
            )}
          </div>
          <p className={`text-sm font-mono font-medium text-${tone}-700`}>{idr(b.total)}</p>
        </div>
      ))}
    </div>
  );
}

function Row({ label, value, bold, big, sub, tone, highlight }) {
  return (
    <div className={`flex justify-between items-baseline py-2 ${highlight ? `border-t border-b border-${tone}-200 bg-${tone}-50 px-3 rounded-lg` : "border-t border-slate-100"}`}>
      <p className={`${bold ? "font-bold" : "font-medium"} text-slate-900 ${big ? "text-lg" : "text-sm"}`}>{label}</p>
      <div className="text-right">
        <p className={`font-mono font-bold ${big ? "text-xl" : "text-sm"} text-${tone || "slate"}-700`}>{idr(value)}</p>
        {sub && <p className={`text-[10px] text-${tone}-700 mt-0.5`}>{sub}</p>}
      </div>
    </div>
  );
}

function Neraca({ periode }) {
  const [data, setData] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  useEffect(() => {
    // Hari terakhir bulan `periode` — perhitungan kalender MURNI lewat
    // `Date.UTC`/`getUTCDate()` (bukan `new Date("YYYY-MM-DD")` + `getDate`/
    // `setDate` lokal + `toISOString()` seperti sebelumnya). Versi lama
    // kebetulan benar untuk browser berzona WIB (offset UTC+7 positif tidak
    // pernah menggeser tanggal kalendernya), tapi salah untuk staf yang
    // laptopnya di-set zona waktu lain (mis. UTC atau Amerika) — perhitungan
    // UTC murni di sini tidak bergantung timezone perangkat sama sekali.
    const y = Number(periode.slice(0, 4)); const m = Number(periode.slice(5, 7));
    const hariTerakhir = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const tanggal = `${y}-${String(m).padStart(2, "0")}-${String(hariTerakhir).padStart(2, "0")}`;
    api.get(`/finance/neraca?tanggal=${tanggal}`).then(r => setData(r.data)).catch(() => toast.error("Gagal memuat"));
  }, [periode]);
  if (!data) return <div className="text-slate-500 text-sm">Memuat...</div>;

  // Pakai `data.tanggal` yang BALIK dari API (bukan dihitung ulang di frontend)
  // supaya nama berkas & query ekspor selalu sinkron dengan snapshot yang
  // sedang ditampilkan di layar.
  const exportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      await _downloadBlob(`/finance/neraca/export-pdf?tanggal=${data.tanggal}`, `neraca-${data.tanggal}.pdf`, "application/pdf");
      toast.success("PDF Neraca berhasil di-download 📄");
    } catch (e) {
      toast.error("Gagal export PDF Neraca");
      console.error(e);
    } finally {
      setExportingPdf(false);
    }
  };
  const exportXlsx = async () => {
    if (exportingXlsx) return;
    setExportingXlsx(true);
    try {
      await _downloadBlob(`/finance/neraca/export-xlsx?tanggal=${data.tanggal}`, `neraca-${data.tanggal}.xlsx`,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      toast.success("Excel Neraca berhasil di-download 📊");
    } catch (e) {
      toast.error("Gagal export Excel Neraca");
      console.error(e);
    } finally {
      setExportingXlsx(false);
    }
  };

  const A = data.aktiva; const P = data.passiva;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm px-5 py-3 flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-slate-500">Neraca per <b className="text-slate-800">{data.tanggal}</b></p>
        <div className="flex items-center gap-2">
          <button
            onClick={exportXlsx}
            disabled={exportingXlsx}
            data-testid="lap-nr-export-xlsx"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm transition disabled:opacity-50"
          >
            {exportingXlsx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
            {exportingXlsx ? "Menyiapkan..." : "Export Excel"}
          </button>
          <button
            onClick={exportPdf}
            disabled={exportingPdf}
            data-testid="lap-nr-export-pdf"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold bg-emerald-700 text-white hover:bg-emerald-800 shadow-sm transition disabled:opacity-50"
          >
            {exportingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {exportingPdf ? "Menyiapkan..." : "Export PDF"}
          </button>
        </div>
      </div>
              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm">
                <div className="px-5 py-3 border-b border-slate-100"><p className="font-display font-bold text-emerald-700">AKTIVA (Harta)</p></div>
                <div className="p-5 space-y-3 text-sm">
                  <p className="text-[11px] uppercase font-semibold text-slate-500">Aset Lancar</p>
                  <NRow label="Kas" v={A.lancar.kas.total} />
                  <NRow label="Bank" v={A.lancar.bank.total} />
                  <NRow label="Petty Cash" v={A.lancar.petty_cash.total} />
                  <NRow label="Piutang" v={A.lancar.piutang.total} />
                  <NRow label="Beban Dibayar di Muka" v={A.lancar.dibayar_dimuka?.total || 0} />
                  <NRow label="Persediaan" v={A.lancar.persediaan.total} />
                  <NRow label="Subtotal Aset Lancar" v={A.lancar.total_lancar} bold />
                  <p className="text-[11px] uppercase font-semibold text-slate-500 pt-2">Aset Tetap ({A.tetap.count_aktif || 0} unit)</p>
                  {A.tetap.perolehan !== undefined && (
                    <div className="pl-3 space-y-1 text-xs bg-slate-50 rounded p-2">
                      <div className="flex justify-between"><span className="text-slate-600">Nilai Perolehan</span><span className="font-mono text-slate-800">{idr(A.tetap.perolehan)}</span></div>
                      <div className="flex justify-between text-rose-700"><span>Akumulasi Penyusutan</span><span className="font-mono">({idr(A.tetap.akumulasi_penyusutan)})</span></div>
                      <div className="flex justify-between border-t border-slate-200 pt-1 font-bold"><span>= Nilai Buku</span><span className="font-mono text-indigo-700">{idr(A.tetap.nilai_buku)}</span></div>
                    </div>
                  )}
                  {A.tetap.breakdown_kategori && A.tetap.breakdown_kategori.length > 0 && (
                    <div className="text-[11px] text-slate-500 space-y-0.5 pl-3">
                      {A.tetap.breakdown_kategori.map((k, i) => (
                        <div key={i} className="flex justify-between"><span>{k.kategori} · {k.count} unit</span><span className="font-mono">{idr(k.buku)}</span></div>
                      ))}
                    </div>
                  )}
                  <NRow label="TOTAL AKTIVA" v={A.total_aktiva} bold big />
                </div>
              </div>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-5 py-3 border-b border-slate-100"><p className="font-display font-bold text-rose-700">PASSIVA (Utang + Modal)</p></div>
        <div className="p-5 space-y-3 text-sm">
          <p className="text-[11px] uppercase font-semibold text-slate-500">Kewajiban</p>
          <NRow label="Utang Usaha" v={P.kewajiban.total} />
          <p className="text-[11px] uppercase font-semibold text-slate-500 pt-2">Ekuitas</p>
          <NRow label="Modal Pemilik" v={P.modal.modal_pemilik.total} />
          <NRow label="Laba Ditahan" v={P.modal.laba_ditahan} />
          <NRow label="Total Ekuitas" v={P.modal.total_ekuitas} bold />
          <NRow label="TOTAL PASSIVA" v={P.total_passiva} bold big />
        </div>
      </div>
      <div className={`lg:col-span-3 p-4 rounded-lg border text-sm ${data.kesehatan === "seimbang" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
        <p className="font-medium">Selisih: {idr(data.selisih)}</p>
        <p className="mt-1">{data.kesimpulan}</p>
      </div>
    </div>
  );
}
function NRow({ label, v, bold, big }) {
  return (
    <div className={`flex justify-between items-baseline ${bold ? "border-t border-slate-200 pt-2 mt-2" : ""}`}>
      <p className={`${bold ? "font-bold" : ""} ${big ? "text-base" : "text-sm"} text-slate-900`}>{label}</p>
      <p className={`font-mono ${bold ? "font-bold" : ""} ${big ? "text-lg text-emerald-700" : "text-sm"}`}>{idr(v)}</p>
    </div>
  );
}

const CF_SECTIONS = [
  { key: "aktivitas_operasi", label: "Aktivitas Operasi" },
  { key: "aktivitas_investasi", label: "Aktivitas Investasi" },
  { key: "aktivitas_pendanaan", label: "Aktivitas Pendanaan" },
];

function CashFlow({ periode }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/finance/cash-flow?periode=${periode}`).then(r => setData(r.data)).catch(() => toast.error("Gagal memuat"));
  }, [periode]);
  if (!data) return <div className="text-slate-500 text-sm">Memuat...</div>;

  const kosong = CF_SECTIONS.every(s => !(data[s.key]?.masuk) && !(data[s.key]?.keluar))
    && !data.kas_awal && !data.kas_akhir;
  const naik = data.net_change > 0;
  const turun = data.net_change < 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="cash-flow-report">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="font-display font-bold text-lg text-slate-900">Laporan Arus Kas</p>
          <p className="text-xs text-slate-500">Periode {data.periode || periode}</p>
        </div>
        <span
          data-testid="cf-net-badge"
          className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
            naik ? "bg-emerald-50 text-emerald-700" : turun ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-500"
          }`}
        >
          {naik ? <TrendingUp className="w-3.5 h-3.5" /> : turun ? <TrendingDown className="w-3.5 h-3.5" /> : null}
          {naik ? "Kas Naik" : turun ? "Kas Turun" : "Kas Stabil"} {idr(Math.abs(data.net_change || 0))}
        </span>
      </div>
      <div className="p-5 space-y-4">
        {kosong ? (
          <div className="text-center py-10 text-slate-400 text-sm" data-testid="cf-empty-state">
            Belum ada transaksi kas pada periode ini.
          </div>
        ) : (
          <>
            <Row label="Kas Awal Periode" value={data.kas_awal} bold />
            {CF_SECTIONS.map(s => (
              <CFSection key={s.key} title={s.label} data={data[s.key]} />
            ))}
            <Row
              label="Kenaikan (Penurunan) Kas Bersih"
              value={data.net_change}
              bold
              tone={data.net_change >= 0 ? "emerald" : "rose"}
            />
            <Row label="Kas Akhir Periode" value={data.kas_akhir} bold big tone={data.kas_akhir >= 0 ? "emerald" : "rose"} highlight />
          </>
        )}
        <div className={`mt-4 p-3 rounded-lg border text-sm ${data.net_change >= 0 ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
          {data.kesimpulan}
        </div>
      </div>
    </div>
  );
}

function CFSection({ title, data }) {
  if (!data) return null;
  const positif = (data.net || 0) >= 0;
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">{title}</p>
      <div className="flex justify-between items-center py-1.5 pl-4 pr-2">
        <p className="text-sm text-slate-700">Kas Masuk</p>
        <p className="text-sm font-mono font-medium text-emerald-700">{idr(data.masuk || 0)}</p>
      </div>
      <div className="flex justify-between items-center py-1.5 pl-4 pr-2">
        <p className="text-sm text-slate-700">Kas Keluar</p>
        <p className="text-sm font-mono font-medium text-rose-700">({idr(data.keluar || 0)})</p>
      </div>
      <div className={`flex justify-between items-center py-2 pl-4 pr-2 border-t border-slate-100 font-semibold text-sm ${positif ? "text-emerald-700" : "text-rose-700"}`}>
        <p>Subtotal {title}</p>
        <p className="font-mono">{idr(data.net || 0)}</p>
      </div>
    </div>
  );
}
